import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar,
  Eye,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { generateMockPromises } from '@/lib/mock-data/collections-mock-data';
import type { PromiseToPay } from '@/types/collections';
import { Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { PromiseDetailsDrawer } from '@/components/collections/promise-details-drawer';
import { CreatePromiseDrawer } from '@/components/collections/create-promise-drawer';

const PromisesPage: React.FC = () => {
  const [promises] = useState<PromiseToPay[]>(generateMockPromises());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedPromise, setSelectedPromise] = useState<PromiseToPay | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [selectedPromiseIds, setSelectedPromiseIds] = useState<Set<string>>(new Set());

  // Calculate KPIs
  const kpis = useMemo(() => {
    const active = promises.filter(p => p.status === 'Active');
    const broken = promises.filter(p => p.status === 'Broken');
    const dueThisWeek = promises.filter(p => {
      const daysUntilDue = Math.ceil((p.promiseDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilDue >= 0 && daysUntilDue <= 7 && p.status === 'Active';
    });
    const atRisk = promises.filter(p => p.isAtRisk);

    return {
      activePromises: {
        count: active.length,
        totalValue: active.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
      brokenPromises: {
        count: broken.length,
        totalValue: broken.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
      dueThisWeek: {
        count: dueThisWeek.length,
        totalValue: dueThisWeek.reduce((sum, p) => sum + p.promisedAmount, 0),
      },
      atRiskPromises: {
        count: atRisk.length,
      },
    };
  }, [promises]);

  // Filter and sort promises based on status filter and search
  const filteredPromises = useMemo(() => {
    let filtered = promises;

    // Filter by status dropdown
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.customerName.toLowerCase().includes(query) ||
        p.customerAccountNumber.toLowerCase().includes(query) ||
        p.promisedAmount.toString().includes(query)
      );
    }

    // Sort by Promise Date in ascending order (oldest first)
    filtered = [...filtered].sort((a, b) => a.promiseDate.getTime() - b.promiseDate.getTime());

    return filtered;
  }, [promises, statusFilter, searchQuery]);

  const handleViewDetails = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setDetailSheetOpen(true);
  };

  const handleEmailCustomer = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setEmailModalOpen(true);
  };

  const handleLogCall = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setCallModalOpen(true);
  };

  const handleCreateTask = (promise: PromiseToPay) => {
    setSelectedPromise(promise);
    setTaskModalOpen(true);
  };

  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { variant: any; className: string }> = {
      Active: { variant: 'default', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
      Broken: { variant: 'destructive', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
      Paid: { variant: 'default', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
      'Partially Paid': { variant: 'secondary', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
    };
    return <Badge className={variants[status].className}>{status}</Badge>;
  };

  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3 mr-1" />,
      Email: <Mail className="h-3 w-3 mr-1" />,
      Portal: <TrendingUp className="h-3 w-3 mr-1" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  const handleCreateNewPromise = (data: any) => {
    console.log('New promise created:', data);
    // In a real application, this would save to the backend
    toast.success('New promise created successfully');
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredPromises.map(p => p.id));
      setSelectedPromiseIds(allIds);
    } else {
      setSelectedPromiseIds(new Set());
    }
  };

  const handleSelectPromise = (promiseId: string, checked: boolean) => {
    const newSelection = new Set(selectedPromiseIds);
    if (checked) {
      newSelection.add(promiseId);
    } else {
      newSelection.delete(promiseId);
    }
    setSelectedPromiseIds(newSelection);
  };

  const isAllSelected = filteredPromises.length > 0 && selectedPromiseIds.size === filteredPromises.length;
  const isSomeSelected = selectedPromiseIds.size > 0 && selectedPromiseIds.size < filteredPromises.length;

  // Bulk action handlers
  const handleBulkViewDetails = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(p => p.id === promiseId);
      if (promise) {
        handleViewDetails(promise);
      }
    } else {
      toast.info(`Viewing details for ${selectedPromiseIds.size} selected promises`);
    }
  };

  const handleBulkEmail = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(p => p.id === promiseId);
      if (promise) {
        handleEmailCustomer(promise);
      }
    } else {
      toast.success(`Email sent to ${selectedPromiseIds.size} customers`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleBulkCall = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(p => p.id === promiseId);
      if (promise) {
        handleLogCall(promise);
      }
    } else {
      toast.info(`Logging calls for ${selectedPromiseIds.size} customers`);
    }
  };

  const handleBulkTask = () => {
    if (selectedPromiseIds.size === 1) {
      const promiseId = Array.from(selectedPromiseIds)[0];
      const promise = promises.find(p => p.id === promiseId);
      if (promise) {
        handleCreateTask(promise);
      }
    } else {
      toast.success(`Created follow-up tasks for ${selectedPromiseIds.size} promises`);
      setSelectedPromiseIds(new Set());
    }
  };

  const handleClearSelection = () => {
    setSelectedPromiseIds(new Set());
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins className="h-8 w-8" />
            Promise to Pay Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Track customer payment commitments and monitor promise fulfillment
          </p>
        </div>
        <Button onClick={() => setCreatePromiseModalOpen(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create New Promise
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Promises</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.activePromises.count}</div>
            <p className="text-xs text-muted-foreground">
              ${kpis.activePromises.totalValue.toLocaleString()} total value
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Broken Promises</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.brokenPromises.count}</div>
            <p className="text-xs text-muted-foreground">
              ${kpis.brokenPromises.totalValue.toLocaleString()} at risk
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
            <Clock className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.dueThisWeek.count}</div>
            <p className="text-xs text-muted-foreground">
              ${kpis.dueThisWeek.totalValue.toLocaleString()} expected
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At Risk</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.atRiskPromises.count}</div>
            <p className="text-xs text-muted-foreground">
              Customers with broken promise history
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Promised Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Promises ({filteredPromises.length})</CardTitle>
          <CardDescription>
            All payment promises sorted by promise date (oldest first)
          </CardDescription>
        </CardHeader>
        
        {/* Search Bar and Status Filter */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by customer name, account number, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Broken">Broken</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all promises"
                        className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                      />
                    </TableHead>
                    <TableHead>Promise ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Case Number</TableHead>
                    <TableHead>Promise Date</TableHead>
                    <TableHead>Days Overdue</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Risk Indicators</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPromises.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        No promises found matching your criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPromises.map((promise) => {
                      const isSelected = selectedPromiseIds.has(promise.id);
                      return (
                        <TableRow 
                          key={promise.id}
                          className={isSelected ? "bg-muted/50" : ""}
                        >
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleSelectPromise(promise.id, checked as boolean)}
                              aria-label={`Select promise ${promise.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleViewDetails(promise)}
                              className="font-mono text-sm text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors"
                            >
                              {promise.id}
                            </button>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleViewDetails(promise)}
                              className="text-left hover:underline text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                            >
                              {promise.customerName} - {promise.customerAccountNumber}
                            </button>
                          </TableCell>
                          <TableCell>
                            {promise.caseNumber ? (
                              <span className="font-mono text-sm text-muted-foreground">
                                {promise.caseNumber}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {format(promise.promiseDate, 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell>
                            {promise.daysOverdue && promise.daysOverdue > 0 ? (
                              <span className="text-red-600 font-medium">
                                {promise.daysOverdue}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              ${promise.promisedAmount.toLocaleString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {promise.invoices.length}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(promise.status)}</TableCell>
                          <TableCell>{getConfirmationMethodBadge(promise.confirmationMethod)}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {promise.isAtRisk && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  At Risk
                                </Badge>
                              )}
                              {promise.brokenPromisesCount && promise.brokenPromisesCount > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {promise.brokenPromisesCount} broken
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
        </CardContent>
      </Card>

      {/* Promise Details Drawer */}
      <PromiseDetailsDrawer
        promise={selectedPromise}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Customer</DialogTitle>
            <DialogDescription>
              Send a reminder email to {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>To</Label>
              <Input value={selectedPromise?.customerEmail || ''} disabled />
            </div>
            <div>
              <Label>Subject</Label>
              <Input placeholder="Payment Reminder" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea placeholder="Enter your message..." rows={6} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Email sent successfully');
              setEmailModalOpen(false);
            }}>
              <Mail className="h-4 w-4 mr-2" />
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Log Modal */}
      <Dialog open={callModalOpen} onOpenChange={setCallModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Call</DialogTitle>
            <DialogDescription>
              Record call details with {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Call Outcome</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="promise-confirmed">Promise Confirmed</SelectItem>
                  <SelectItem value="promise-broken">Promise Broken</SelectItem>
                  <SelectItem value="no-answer">No Answer</SelectItem>
                  <SelectItem value="voicemail">Left Voicemail</SelectItem>
                  <SelectItem value="dispute">Dispute Raised</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Call notes..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Call logged successfully');
              setCallModalOpen(false);
            }}>
              Save Call Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-up Task</DialogTitle>
            <DialogDescription>
              Schedule a follow-up action for {selectedPromise?.customerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Task Type</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select task type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Follow-up Call</SelectItem>
                  <SelectItem value="email">Send Email</SelectItem>
                  <SelectItem value="review">Review Promise</SelectItem>
                  <SelectItem value="escalate">Escalate Case</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea placeholder="Task details..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskModalOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              toast.success('Task created successfully');
              setTaskModalOpen(false);
            }}>
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseModalOpen}
        onOpenChange={setCreatePromiseModalOpen}
        onSave={handleCreateNewPromise}
      />

      {/* Floating Actions Bar */}
      {selectedPromiseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <Card className="shadow-lg border-2">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm font-semibold">
                    {selectedPromiseIds.size} Selected
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearSelection}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="h-8 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkViewDetails}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View Details
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkEmail}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkCall}
                    className="gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Log Call
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkTask}
                    className="gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Create Task
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PromisesPage;